import { useState } from "react";
import axios from "axios";
import {
  ArrowLeft,
  Check,
  Circle,
  Lightbulb,
  Lock,
  Loader2,
  Rocket,
  Star,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import ComuneAutocomplete from "../ComuneAutocomplete";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_STEPS = [
  { id: "analisi", label: "Analisi completata", status: "done" },
  { id: "design", label: "Design completato", status: "done" },
  { id: "sviluppo", label: "Sviluppo in corso", status: "active" },
  { id: "beta", label: "Beta privata", status: "todo" },
  { id: "lancio", label: "Lancio pubblico", status: "todo" },
];

function ProjectStatus({ theme, compact = false }) {
  const isDark = theme === "personale";
  return (
    <div
      className={`rounded-xl border ${compact ? "p-3" : "p-4"} ${
        isDark ? "bg-white/5 border-white/10" : "bg-white border-slate-200 shadow-sm"
      }`}
    >
      <p
        className={`text-[10px] font-semibold uppercase tracking-wider mb-2.5 ${
          isDark ? "text-violet-300" : "text-slate-500"
        }`}
      >
        Stato del progetto
      </p>
      <ol className={compact ? "space-y-2" : "space-y-2.5"}>
        {PROJECT_STEPS.map((step, idx) => {
          const isLast = idx === PROJECT_STEPS.length - 1;
          return (
            <li key={step.id} className="flex gap-2.5">
              <div className="flex flex-col items-center">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    step.status === "done"
                      ? "bg-emerald-500 text-white"
                      : step.status === "active"
                        ? isDark
                          ? "bg-violet-500 text-white ring-2 ring-violet-500/30"
                          : "bg-teal-600 text-white ring-2 ring-teal-600/20"
                        : isDark
                          ? "border-2 border-white/25"
                          : "border-2 border-slate-300"
                  }`}
                >
                  {step.status === "done" ? (
                    <Check className="w-3 h-3" />
                  ) : step.status === "active" ? (
                    <Circle className="w-2 h-2 fill-current" />
                  ) : null}
                </div>
                {!isLast && (
                  <div
                    className={`w-px flex-1 min-h-[8px] mt-0.5 ${
                      step.status === "done"
                        ? "bg-emerald-400/70"
                        : isDark
                          ? "bg-white/15"
                          : "bg-slate-200"
                    }`}
                  />
                )}
              </div>
              <span
                className={`text-xs leading-5 ${
                  step.status === "active"
                    ? isDark
                      ? "text-white font-semibold"
                      : "text-slate-900 font-semibold"
                    : step.status === "done"
                      ? isDark
                        ? "text-slate-200"
                        : "text-slate-700"
                      : isDark
                        ? "text-slate-400"
                        : "text-slate-400"
                }`}
              >
                {step.label}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function ServizioAnteprimaPanel({
  config,
  token,
  existingRequest,
  onSubmitted,
  onClose,
  onExplore,
}) {
  const isDark = config.theme === "personale";
  const [step, setStep] = useState("welcome"); // welcome | form
  const [richiestaAttuale, setRichiestaAttuale] = useState("");
  const [comune, setComune] = useState("");
  const [provincia, setProvincia] = useState("");
  const [consenso, setConsenso] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuggerimento, setShowSuggerimento] = useState(false);
  const [suggerimento, setSuggerimento] = useState("");
  const [sendingSuggerimento, setSendingSuggerimento] = useState(false);

  const accentBtn = isDark
    ? "bg-violet-600 hover:bg-violet-500 text-white"
    : "bg-[#0F766E] hover:bg-teal-700 text-white";
  const badgeClass = isDark
    ? "bg-violet-600 text-white"
    : "bg-teal-100 text-teal-800";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!richiestaAttuale.trim()) {
      toast.error("Indica la richiesta attuale della struttura");
      return;
    }
    if (!comune.trim() || !provincia.trim()) {
      toast.error("Seleziona comune e provincia");
      return;
    }
    if (!consenso) {
      toast.error("È necessario il consenso al contatto");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/trova-servizi/${config.servizio}/accesso`,
        {
          richiesta_attuale: richiestaAttuale.trim(),
          comune: comune.trim(),
          provincia: provincia.trim(),
          consenso_contatto: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.already_requested) {
        toast.info(data.message || "Richiesta già presente");
      } else {
        toast.success("Richiesta di accesso anticipato inviata");
      }
      onSubmitted?.(data.richiesta);
      setStep("welcome");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'invio");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuggerimento = async () => {
    if (!suggerimento.trim()) {
      toast.error("Scrivi un suggerimento");
      return;
    }
    setSendingSuggerimento(true);
    try {
      await axios.post(
        `${API_URL}/api/trova-servizi/${config.servizio}/suggerimento`,
        { suggerimento: suggerimento.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Grazie! Suggerimento inviato");
      setSuggerimento("");
      setShowSuggerimento(false);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'invio");
    } finally {
      setSendingSuggerimento(false);
    }
  };

  const sidebarBg = isDark
    ? "bg-[#0A1941]"
    : "bg-[#D8F3E8]";
  const sidebarTitle = isDark ? "Il personale giusto, più in fretta" : "Insieme siamo più forti";
  const sidebarText = isDark
    ? "Professionisti socio-sanitari verificati, a portata di click per la tua struttura."
    : "Più siamo, più opportunità possiamo offrire alle famiglie e alle strutture.";

  return (
    <div
      className={`relative w-full max-w-[920px] rounded-2xl shadow-2xl overflow-hidden max-h-[min(92vh,720px)] flex flex-col ${
        isDark
          ? "bg-slate-900 text-white border border-white/10"
          : "bg-white text-slate-900 border border-slate-200"
      }`}
    >
      <button
        type="button"
        onClick={onClose || onExplore}
        className={`absolute top-3 right-3 z-20 p-1.5 rounded-lg transition-colors ${
          isDark ? "hover:bg-white/10 text-slate-300" : "hover:bg-slate-100 text-slate-500"
        }`}
        aria-label="Chiudi"
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex flex-col md:flex-row min-h-0 flex-1 overflow-hidden">
        {/* Left sidebar — as in mockup */}
        <aside
          className={`hidden md:flex w-[240px] lg:w-[260px] flex-shrink-0 flex-col justify-between p-5 ${sidebarBg}`}
        >
          <div className="flex-1 flex items-center justify-center py-4 px-2">
            <img
              src={config.illustrationSrc}
              alt={config.illustrationAlt || config.title}
              className={`w-full max-w-[220px] h-auto object-contain ${
                isDark ? "" : "mix-blend-multiply"
              }`}
              draggable={false}
            />
          </div>
          <div className={`pt-4 border-t ${isDark ? "border-white/10" : "border-teal-900/10"}`}>
            <div className="flex items-start gap-2.5">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDark ? "bg-violet-500/30 text-violet-200" : "bg-white/80 text-teal-700"
                }`}
              >
                <Users className="w-4 h-4" />
              </div>
              <div>
                <p className={`text-sm font-bold leading-snug ${isDark ? "text-white" : "text-teal-950"}`}>
                  {sidebarTitle}
                </p>
                <p className={`text-xs mt-1 leading-relaxed ${isDark ? "text-slate-300" : "text-teal-900/75"}`}>
                  {sidebarText}
                </p>
              </div>
            </div>
          </div>
        </aside>

        {/* Right content */}
        <div className="flex-1 min-w-0 overflow-y-auto p-5 md:p-6 lg:pr-8">
          {step === "form" && !existingRequest ? (
            <form onSubmit={handleSubmit} className="space-y-3 max-w-xl">
              <button
                type="button"
                onClick={() => setStep("welcome")}
                className={`inline-flex items-center gap-1.5 text-xs font-medium mb-1 ${
                  isDark ? "text-violet-300 hover:text-violet-200" : "text-teal-700 hover:text-teal-800"
                }`}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Torna al riepilogo
              </button>
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide ${badgeClass}`}
              >
                {config.badgeLabel}
              </span>
              <h2 className="text-xl font-bold tracking-tight">Richiedi accesso anticipato</h2>
              <p className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                Compila il form: ti contatteremo per attivare {config.title} sulla tua struttura.
              </p>

              <div className="space-y-1.5">
                <Label className={`text-xs ${isDark ? "text-slate-200" : undefined}`}>
                  Richiesta attuale della struttura *
                </Label>
                <Textarea
                  value={richiestaAttuale}
                  onChange={(e) => setRichiestaAttuale(e.target.value)}
                  placeholder={config.richiestaPlaceholder}
                  rows={2}
                  required
                  className={
                    isDark
                      ? "bg-slate-950/50 border-white/15 text-white placeholder:text-slate-500 min-h-[64px]"
                      : "bg-white min-h-[64px]"
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className={`text-xs ${isDark ? "text-slate-200" : undefined}`}>
                  Provincia / Comune *
                </Label>
                <ComuneAutocomplete
                  value={comune}
                  onChange={(v) => {
                    setComune(v);
                    if (!v) setProvincia("");
                  }}
                  onSelect={(c) => {
                    setComune(c.descrizione || c.label || "");
                    setProvincia(c.provincia || "");
                  }}
                  placeholder="Cerca comune…"
                  className={
                    isDark
                      ? "bg-slate-950/50 border-white/15 text-white placeholder:text-slate-500"
                      : ""
                  }
                />
                {provincia ? (
                  <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Provincia: <strong>{provincia}</strong>
                  </p>
                ) : null}
              </div>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <Checkbox
                  checked={consenso}
                  onCheckedChange={(v) => setConsenso(Boolean(v))}
                  className={isDark ? "border-white/40 data-[state=checked]:bg-violet-600" : undefined}
                />
                <span className={`text-xs leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  Consenso al contatto: acconsento a essere ricontattato dal team Affidya in merito
                  a questo servizio in anteprima. *
                </span>
              </label>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button type="submit" disabled={submitting} className={`flex-1 h-10 ${accentBtn}`}>
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Rocket className="w-4 h-4 mr-2" />
                  )}
                  Invia richiesta
                </Button>
                {onExplore && (
                  <Button
                    type="button"
                    variant="outline"
                    className={`h-10 ${
                      isDark
                        ? "border-white/30 bg-transparent text-white hover:bg-white/10"
                        : "border-slate-300 bg-white"
                    }`}
                    onClick={onExplore}
                  >
                    Continua l&apos;esplorazione
                  </Button>
                )}
              </div>
            </form>
          ) : (
            <>
              <div className={`md:hidden mb-3 flex items-center gap-3 rounded-xl px-2 py-2 ${
                isDark ? "bg-[#0A1941]" : "bg-[#D8F3E8]"
              }`}>
                <img
                  src={config.illustrationSrc}
                  alt=""
                  className={`w-16 h-16 object-contain ${isDark ? "" : "mix-blend-multiply"}`}
                />
                <p className={`text-xs leading-snug ${isDark ? "text-slate-300" : "text-teal-900/80"}`}>
                  <span className="font-semibold block">{sidebarTitle}</span>
                  {sidebarText}
                </p>
              </div>

              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wide ${badgeClass}`}
              >
                {config.badgeLabel}
              </span>
              <h2 className={`mt-2 text-xl md:text-2xl font-bold tracking-tight ${isDark ? "text-white" : "text-slate-900"}`}>
                {config.welcomeTitle}
              </h2>
              {config.tagline && (
                <p
                  className={`mt-1.5 text-sm font-medium leading-snug ${
                    isDark ? "text-violet-200" : "text-teal-700"
                  }`}
                >
                  {config.tagline}
                </p>
              )}
              <p
                className={`mt-1.5 text-xs md:text-sm leading-relaxed max-w-2xl ${
                  isDark ? "text-slate-300" : "text-slate-600"
                }`}
              >
                {config.description}
              </p>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-4 items-start">
                <ul className="space-y-2.5">
                  {config.benefits.map((b) => (
                    <li key={b.title} className="flex gap-2.5">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isDark ? "bg-violet-500/25 text-violet-300" : "bg-teal-100 text-teal-700"
                        }`}
                      >
                        <b.icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="min-w-0 pt-0.5">
                        <p className={`text-sm font-semibold leading-tight ${isDark ? "text-white" : "text-slate-800"}`}>
                          {b.title}
                        </p>
                        <p className={`text-xs mt-0.5 leading-snug ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                          {b.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
                <ProjectStatus theme={config.theme} compact />
              </div>

              <div
                className={`mt-4 rounded-xl px-3 py-2.5 ${
                  isDark
                    ? "bg-violet-500/10 border border-violet-400/20"
                    : "bg-teal-50 border border-teal-100"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex gap-2 min-w-0 items-center">
                    {isDark ? (
                      <Lightbulb className="w-4 h-4 flex-shrink-0 text-amber-300" />
                    ) : (
                      <Star className="w-4 h-4 flex-shrink-0 text-teal-600 fill-teal-600/20" />
                    )}
                    <div>
                      <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-teal-900"}`}>
                        Aiutaci a migliorare
                      </p>
                      <p className={`text-[11px] ${isDark ? "text-slate-300" : "text-teal-800/80"}`}>
                        Hai suggerimenti o idee? Siamo qui per ascoltarti.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSuggerimento((v) => !v)}
                    className={
                      isDark
                        ? "border-white/30 bg-transparent text-white hover:bg-white/10 h-8"
                        : "border-teal-200 bg-white text-teal-800 hover:bg-teal-100 h-8"
                    }
                  >
                    Invia suggerimento
                  </Button>
                </div>
                {showSuggerimento && (
                  <div className="mt-2.5 space-y-2">
                    <Textarea
                      value={suggerimento}
                      onChange={(e) => setSuggerimento(e.target.value)}
                      placeholder="Scrivi qui il tuo suggerimento…"
                      rows={2}
                      className={
                        isDark
                          ? "bg-slate-950/50 border-white/15 text-white placeholder:text-slate-500"
                          : "bg-white"
                      }
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSuggerimento}
                      disabled={sendingSuggerimento}
                      className={accentBtn}
                    >
                      {sendingSuggerimento ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Invia
                    </Button>
                  </div>
                )}
              </div>

              {existingRequest ? (
                <div
                  className={`mt-4 rounded-xl p-4 text-center ${
                    isDark
                      ? "bg-violet-500/15 border border-violet-400/25"
                      : "bg-teal-50 border border-teal-200"
                  }`}
                >
                  <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-teal-900"}`}>
                    Richiesta di accesso già inviata
                  </p>
                  <p className={`text-xs mt-1 ${isDark ? "text-slate-300" : "text-teal-800/80"}`}>
                    Stato: <strong>{existingRequest.stato || "in attesa"}</strong>
                    {existingRequest.comune
                      ? ` · ${existingRequest.comune}${
                          existingRequest.provincia ? ` (${existingRequest.provincia})` : ""
                        }`
                      : ""}
                  </p>
                  {onExplore && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={`mt-3 ${
                        isDark ? "border-white/30 text-white hover:bg-white/10" : "border-slate-300"
                      }`}
                      onClick={onExplore}
                    >
                      Continua l&apos;esplorazione
                    </Button>
                  )}
                </div>
              ) : (
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Button
                    type="button"
                    className={`flex-1 h-10 font-semibold ${accentBtn}`}
                    onClick={() => setStep("form")}
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Richiedi accesso anticipato
                  </Button>
                  {onExplore && (
                    <Button
                      type="button"
                      variant="outline"
                      className={`h-10 sm:min-w-[180px] ${
                        isDark
                          ? "border-white/30 bg-transparent text-white hover:bg-white/10"
                          : "border-slate-300 bg-white"
                      }`}
                      onClick={onExplore}
                    >
                      Continua l&apos;esplorazione
                    </Button>
                  )}
                </div>
              )}

              <p
                className={`mt-3 flex items-center justify-center gap-1.5 text-[10px] ${
                  isDark ? "text-slate-500" : "text-slate-400"
                }`}
              >
                <Lock className="w-3 h-3" />
                Il servizio sarà attivato senza costi aggiuntivi per gli utenti Affidya.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
